import React, { useEffect, useState } from 'react';
import {
    Card,
    CardContent,
    Typography,
    Box,
    List,
    ListItem,
    ListItemText,
    Divider,
    Button,
    IconButton,
    Tooltip,
    Chip,
    Grid,
    keyframes,
    Link
} from '@mui/material';
import { Person, Groups, AdminPanelSettings, EditOutlined, CheckCircle, Male, Female } from '@mui/icons-material';
import { Trans, useTranslation } from 'react-i18next';
import MembershipModal from './MembershipModal';
import { FormProvider, useForm } from 'react-hook-form';
import { useNavigate } from 'react-router';
import { isAdult } from 'qubCommon/helperMethods/isAdult';
import Conditional from 'common/Conditional';
import format from 'date-fns/format';
import DependentInvitationDialog from './DependentInvitationDialog';
import { useSnackbar } from 'notistack';
import { releaseAccount } from 'apis/controllers/membership';

// Define keyframes for pulse effect
const pulseGlow = keyframes`
  0% { box-shadow: 0 0 10px rgba(76, 175, 80, 0.5); }
  50% { box-shadow: 0 0 30px rgba(76, 175, 80, 0.9); }
  100% { box-shadow: 0 0 10px rgba(76, 175, 80, 0.5); }
`;

const MembershipCard = ({ member, onRenewMembership, onRemoveMembership, invalidate }) => {
    const { t } = useTranslation();
    const {
        avatar,
        firstName,
        lastName,
        dob,
        roles,
        isUser,
        membershipId,
        membershipType,
        membershipAmount,
        membershipDiscount = null,
        isActiveMember,
        pendingPayment = false,
        expiresAt,
        products,
        leadUserId = null,
        discountCode,
        discountComments,
        canRenew = true,
        authCode = null,
        canInvite = false,
        email = '',
        gender = null
    } = member;

    const dependentAdult = !isUser && isAdult(format(new Date(dob), 'dd/MM/yyyy'));

    const [openModal, setOpenModal] = useState(false);
    const [inviteDialogOpen, setInviteDialogOpen] = useState(false);
    const { enqueueSnackbar } = useSnackbar();

    const handleOpenModal = () => setOpenModal(true);
    const handleCloseModal = () => setOpenModal(false);
    const handleRenewSubmit = (selectedMembership, amount, label, code, comments, discount) => {
        onRenewMembership(member.id, selectedMembership, amount, ` ${firstName} ${lastName} - ${label}`, code, comments, discount);
        setOpenModal(false);
    };
    const handleOpenInviteDialog = () => setInviteDialogOpen(true);
    const handleCloseInviteDialog = () => setInviteDialogOpen(false);
    const handleInviteDependent = async (email) => {
        const res = await releaseAccount({ email, personId: member?.id });
        console.log(res);
        if (res?.status === 'success') invalidate();
        else {
            enqueueSnackbar(t(res?.message), { variant: 'error' });
            return 'error';
        }
        return 'success';
    };

    const form = useForm();

    useEffect(() => {
        if (membershipType !== form?.watch('membershipType')) {
            form.setValue('membershipType', membershipType);
            form.setValue('membershipTypeSubmitted', membershipType);
        }
        if (discountCode) form.setValue('discountCode', discountCode);
        if (discountComments) form.setValue('discountComments', discountComments);
    }, []);

    let expireDate = expiresAt;

    try {
        expireDate = format(new Date(expireDate), 'yyyy-MM-dd');
    } catch (e) {}

    const getBorderColor = () => {
        if (isActiveMember) return '#2E7D32'; // Dark Green for active membership
        if (!isActiveMember && pendingPayment) return '#FF9800'; // Orange for pending payment
        return '#BDBDBD'; // Gray for inactive membership
    };

    const getChip = () => {
        if (isActiveMember) return 'Active';
        if (!isActiveMember && pendingPayment) return 'Pending Payment';
        return 'Inactive';
    };

    const navigate = useNavigate();
    const handleEditPerson = () => {
        navigate(`/profile/${member?.id}`);
    };

    const getGender = () => {
        switch (gender?.toLowerCase()) {
            case 'male':
            case 'm':
                return (
                    <div style={{ display: 'flex', flexDirection: 'row', alignItems: 'center' }}>
                        Male <Male sx={{ verticalAlign: 'middle', fontSize: 20, color: 'primary.main', ml: 1 }} />{' '}
                    </div>
                );
            case 'female':
            case 'f':
                return (
                    <div style={{ display: 'flex', flexDirection: 'row', alignItems: 'center' }}>
                        Female <Female sx={{ verticalAlign: 'middle', fontSize: 20, color: 'secondary.main', ml: 1 }} />
                    </div>
                );
                return null;
        }
    };

    return (
        <Card
            elevation={isActiveMember ? 10 : 3} // More elevated when active
            sx={{
                borderRadius: 3,
                border: `4px solid ${getBorderColor()}`,
                height: '100%',
                display: 'flex',
                background: isActiveMember ? 'linear-gradient(135deg, #A5D6A7,rgb(157, 255, 162))' : 'white',
                animation: isActiveMember ? `${pulseGlow} 2.5s infinite ease-in-out` : 'none',
                transition: 'all 0.3s ease'
            }}
        >
            <CardContent sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', paddingBottom: '12px !important' }}>
                {/* Header with Edit Icon */}
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        {avatar ? (
                            <img src={avatar} style={{ width: 100, marginRight: 20, borderRadius: 20 }} />
                        ) : (
                            <>
                                {isUser ? (
                                    <Person sx={{ fontSize: 100, color: 'primary.main', mr: 2 }} />
                                ) : (
                                    <Groups sx={{ fontSize: 100, color: 'primary.main', mr: 2 }} />
                                )}
                            </>
                        )}
                        <Box>
                            <Typography variant="h2" sx={{ color: 'black !important' }} fontWeight="bold">
                                {firstName} {lastName}
                            </Typography>
                            <Typography variant="h4">
                                {getGender()}
                                {dob}
                            </Typography>
                        </Box>
                    </Box>
                    {(!isAdult(format(new Date(dob), 'dd/MM/yyyy')) || isUser) && (
                        <Tooltip title={t('EditPerson')}>
                            <IconButton sx={{ bgcolor: '#E3F2FD' }} color="primary" onClick={handleEditPerson}>
                                <EditOutlined />
                            </IconButton>
                        </Tooltip>
                    )}
                </Box>

                <Divider sx={{ mb: 2 }} />

                {/* Roles */}
                <Typography variant="body2" sx={{ mb: 1 }}>
                    {t('Member.Homepage.Roles')}
                </Typography>
                {roles.length > 0 ? (
                    <List>
                        {roles.map((role, index) => (
                            <ListItem key={index} sx={{ py: 0 }}>
                                <AdminPanelSettings sx={{ fontSize: 20, color: 'text.secondary', mr: 1 }} />
                                <ListItemText primary={role} />
                            </ListItem>
                        ))}
                    </List>
                ) : (
                    <Typography color="textSecondary">{t('NoRolesAssigned')}</Typography>
                )}

                {/* Display Membership Amount */}
                {membershipAmount ? (
                    <Typography sx={{ mt: 2, fontSize: 16, fontWeight: 'bold', color: '#1B5E20' }}>
                        {products?.find((prod) => prod.id === form?.watch('membershipTypeSubmitted'))?.label}
                        <span
                            style={
                                membershipDiscount ? { textDecoration: 'line-through', color: 'darkred', marginLeft: 4 } : { marginLeft: 4 }
                            }
                        >
                            {membershipAmount.toFixed(2)} CAD
                        </span>
                        {membershipDiscount ? (
                            <span style={{ marginLeft: 10 }}>
                                ${(membershipAmount - membershipAmount * membershipDiscount).toFixed(2)} CAD
                            </span>
                        ) : null}
                    </Typography>
                ) : null}
                <Conditional condition={authCode}>
                    <Typography variant="h4" mt={3} color="primary">
                        {t('MailInvited')}: {email}
                    </Typography>
                    <Typography variant="h4" mt={1} mb={1} color="primary">
                        {t('InvitationPin')}: {authCode}
                    </Typography>
                </Conditional>

                {/* Button for Renew/Edit Membership */}
                <Grid
                    container
                    sx={{
                        marginTop: 'auto',
                        display: 'flex',
                        flexWrap: 'wrap',
                        gap: 1,
                        alignItems: 'center',
                        justifyContent: 'space-between'
                    }}
                >
                    <Conditional condition={!isAdult(format(new Date(dob), 'dd/MM/yyyy')) || isUser}>
                        {!isActiveMember && (
                            <Button
                                disabled={!canRenew}
                                startIcon={<EditOutlined />}
                                color={isActiveMember || pendingPayment ? 'primary' : 'success'}
                                onClick={handleOpenModal}
                                variant="contained"
                                sx={{
                                    minWidth: 140,
                                    flexGrow: 1,
                                    background: isActiveMember ? '#1B5E20' : undefined, // Dark Green if Active
                                    '&:hover': {
                                        background: isActiveMember ? '#2E7D32' : undefined
                                    }
                                }}
                            >
                                {isActiveMember || pendingPayment ? t('EditMembership') : t('RenewMembership')}
                            </Button>
                        )}

                        {pendingPayment && (
                            <Button
                                onClick={() => onRemoveMembership(member?.id, form?.watch('membershipType'))}
                                variant="contained"
                                color="error"
                                sx={{ minWidth: 100, flexGrow: 1 }}
                            >
                                {t('Cancel')}
                            </Button>
                        )}
                    </Conditional>

                    {/* Chip with Icon */}
                    <Conditional condition={canInvite}>
                        <DependentInvitationDialog
                            open={inviteDialogOpen}
                            setOpen={setInviteDialogOpen}
                            onClose={handleCloseInviteDialog}
                            handleInviteDependent={handleInviteDependent}
                        />
                        <Button variant="contained" onClick={handleOpenInviteDialog}>
                            {t('InviteDependent')}
                        </Button>
                    </Conditional>
                    <Chip
                        icon={isActiveMember ? <CheckCircle color="white" sx={{ color: '#fefefe' }} /> : null}
                        label={getChip()}
                        sx={{
                            bgcolor: isActiveMember ? '#2E7D32' : getBorderColor(),
                            color: '#fefefe',
                            fontWeight: 'bold',
                            flexGrow: 1,
                            fontSize: isActiveMember ? '1.1rem' : '0.95rem',
                            px: 2,
                            py: 1.2,
                            borderRadius: 3
                        }}
                    />
                </Grid>
                <Typography mt={1}>{`${isActiveMember ? '' : ''}${expiresAt ? `${t('ExpiresAt')}: ${expireDate}` : ''}`}</Typography>
                {!canRenew && !dependentAdult ? (
                    <Trans
                        t={t}
                        i18nKey="MemberShipDashboard.CannotRenew.Message"
                        values={{}}
                        components={{
                            emailLink: <Link href={`/profile/${member?.id}`} />,
                            p: <Typography color="error" fontWeight="bold" />
                        }}
                    />
                ) : null}
            </CardContent>

            {/* Membership Modal */}
            <FormProvider {...form}>
                <MembershipModal
                    open={openModal}
                    onClose={handleCloseModal}
                    onSubmit={handleRenewSubmit}
                    products={products}
                    membershipAmount={membershipAmount}
                    membershipType={membershipType}
                    leadUserId={leadUserId}
                    membershipDiscount={membershipDiscount}
                    discountCodeDefault={discountCode}
                    discountCommentsDefault={discountComments}
                />
            </FormProvider>
        </Card>
    );
};

export default MembershipCard;
